/*
PURPOSE: Generate consistent, attractive color schemes for users and page types

USE CASES:
- Generate unique but consistent colors for user profiles based on their name
- Provide type-based color schemes for different page types (artist, profile, venue, label)
- Ensure accessibility and visual harmony across the platform

DOMAIN BOUNDARIES:
- Responsible for color generation and palette management
- Provides consistent color hashing algorithms
- Maintains brand-aligned color schemes for different contexts
*/

// Simple hash function to convert string to number
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Generate HSL color from name hash
function generateUserColor(name: string): { 
  primary: string; 
  secondary: string; 
  background: string;
  text: string;
} {
  const hash = simpleHash(name.toLowerCase());
  
  // Generate hue from hash (avoiding reds/oranges which are brand colors)
  // Use range 120-300 to avoid coral colors (0-60) and get nice blues/greens/purples
  const hue = 120 + (hash % 180);
  
  // Use consistent saturation and lightness for pleasing colors
  const saturation = 65 + (hash % 20); // 65-85%
  const lightness = 45 + (hash % 15); // 45-60%
  
  return {
    primary: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    secondary: `hsl(${hue}, ${saturation - 20}%, ${lightness + 20}%)`,
    background: `hsl(${hue}, ${saturation - 40}%, 95%)`,
    text: `hsl(${hue}, ${saturation}%, 25%)`
  };
}

// Type-based color schemes
export const pageColorSchemes = {
  artist: {
    gradient: 'bg-gradient-to-b from-coral-pink/15 via-coral-orange/8 to-white',
    headerGradient: 'bg-gradient-to-r from-coral-pink to-coral-orange',
    accent: 'coral-pink',
    secondary: 'coral-orange'
  },
  profile: {
    gradient: 'bg-gradient-to-br from-coral-pink/20 via-purple-500/10 to-coral-pink/20',
    headerGradient: 'bg-gradient-to-r from-coral-pink to-purple-500',
    accent: 'coral-pink',
    secondary: 'purple-500'
  },
  venue: {
    gradient: 'bg-gradient-to-br from-blue-400/10 via-white to-sky-400/10',
    headerGradient: 'bg-gradient-to-r from-blue-500 to-sky-500',
    accent: 'blue-500',
    secondary: 'sky-500'
  },
  label: {
    gradient: 'bg-gradient-to-br from-purple-400/10 via-white to-violet-400/10',
    headerGradient: 'bg-gradient-to-r from-purple-500 to-violet-500',
    accent: 'purple-500',
    secondary: 'violet-500'
  }
} as const;

export type PageType = keyof typeof pageColorSchemes;

// Generate user-specific colors
export function getUserColors(userName: string) {
  return generateUserColor(userName);
}

// Get page color scheme
export function getPageColorScheme(pageType: PageType) {
  return pageColorSchemes[pageType];
}

// Generate avatar background color for user
export function getUserAvatarStyle(userName: string): React.CSSProperties {
  const colors = getUserColors(userName);
  return {
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
    color: 'white'
  };
}

// Generate user initials from name
export function getUserInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}